// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { ObjectTypeV1 as ObjectType, UserInput, UserOutput, AdminPermissionsEnum, PermissionsEnum } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import { UserCreate } from '@Organisms/User/UserCreate/UserCreate';
import UserListCheckboxCell from '@Organisms/User/UserListCheckboxCell/UserListCheckboxCell';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { computeEventList } from '@Organisms/User/eventList';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import ActionList from '@Molecules/ActionList/ActionList';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import useUserPermissions from '@Hooks/useUserPermissions';
import FormatPermissions from '@Molecules/Format/FormatPermissions/FormatPermissions';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { ICONS } from '@Constants/iconsApp';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { GlobalUserDetails } from '@Organisms/User/GlobalUserDetails/GlobalUserDetails';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { StatusContext } from '@Context/StatusProvider';
import { useDrawer } from '@Hooks/useDrawer';
import { GlobalUserDrawer } from '@Organisms/User/GlobalUserDrawer/GlobalUserDrawer';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import UserMFAButton from '@Atoms/Buttons/UserMFAButton/UserMFAButton';
import usePageTitle from '@Hooks/usePageTitle';

import './UserList.scss';

const UserList = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();
    const { quotas } = useContext(StatusContext);

    const { visible, setVisible, data, setData } = useDrawer<UserOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<UserOutput>({});

    const config = useConfigList<UserOutput>({
        selector: {
            head: [{ name: 'getOrganisation', idOrName: orgId }, { name: 'users' }],
            options: { organisation: orgId },
            name: `get-${orgId}-users`,
            sort: ['+login'],
        },
        controller: client.UserController,
        storageId: StorageFilterEnum.USERS,
        objectType: ObjectType.USER,
    });

    const {
        dataSource = [],
        columnsRenderer,
        actionListRenderer,
        rowSelectionCustomRenderer,
        listRefetch,
        countRefetch,
        status = 'loading',
        table,
        filterManager,
        paginationManager,
        stateSelectedKeys: { selectedKeys },
        storagePageManager,
        storageViewManager,
        queryParams,
    } = config;

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const { setDefaultDisplay } = storageViewManager || {};

    const { page: storagePage, setDisplay, setOnLoadView, setFilterIsOpen } = storagePageManager || { page: null };

    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_USER] });

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    setOnLoadView?.((viewContent) => {
        loadVisibleStateDates({
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const userIdList = selectedItems.map(({ _id }) => _id);
        const isMultiple = userIdList.length > 1 ? 'multiple' : 'single';

        return {
            delete: {
                query: (): Promise<void> =>
                    client.UserController.multipleDelete(userIdList, orgId)
                        .then(() => {
                            openMessage('success', i('notification.user.delete'));
                            onSuccess(true);
                        })
                        .catch(handleNotificationError),
                message: i(`user.actions.delete.${isMultiple}`),
                label: i('user.actions.delete.label'),
                icon: <ICONS.DeleteIcon color="red" />,
                confirm: true,
            },
            lock: {
                query: (): Promise<void> =>
                    client.UserController.multipleEdit({ locked: true }, userIdList)
                        .then(() => {
                            openMessage('success', i('notification.user.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`user.actions.lock.${isMultiple}`),
                label: i('user.actions.lock.label'),
                icon: <ICONS.LockIcon />,
                hidden: selectedItems.some(({ locked }) => locked),
                confirm: true,
            },
            unlock: {
                query: (): Promise<void> =>
                    client.UserController.multipleEdit({ locked: false }, userIdList)
                        .then(() => {
                            openMessage('success', i('notification.user.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`user.actions.unlock.${isMultiple}`),
                label: i('user.actions.unlock.label'),
                icon: <ICONS.UnlockIcon />,
                hidden: selectedItems.some(({ locked }) => !locked),
            },
        };
    });

    const onSuccess = useCallback(
        (count?: boolean, label?: string) => {
            if (label) {
                openMessage('success', label);
            }
            if (count) {
                countRefetch();
            }
            listRefetch();
        },
        [listRefetch, countRefetch],
    );

    const eventList = useMemo(
        () =>
            computeEventList({
                locale: i,
                controller: client.UserController,
                onSuccess,
                onError: handleNotificationError,
            }),
        [i, onSuccess],
    );

    const columns = columnsRenderer([
        {
            title: [
                { label: i('user.list.table.columns.details') },
                { label: i('user.list.table.columns.fullname'), sortField: 'name' },
                { label: i('user.list.table.columns.login'), sortField: 'login' },
            ],
            className: 'user-list__column-details column_preview',
            render: (user) => (
                <GlobalUserDetails
                    data={user}
                    onPreviewClick={() => {
                        setVisible(true);
                        setData(user);
                    }}
                />
            ),
        },
        {
            title: [{ label: i('user.list.table.columns.profile'), sortField: `profile.${orgId}` }],
            className: 'user-list__column-profile',
            render: ({ profile = '', permissions = [] }): ReactElement => (
                <FormatPermissions profile={profile} permissions={permissions} />
            ),
        },
        {
            title: [{ label: i('user.list.table.columns.mfa.label'), sortField: 'hasMFA' }],
            className: 'user-list__column-mfa',
            render: (data, { bool: filterBool }) => {
                const { hasMFA } = data;

                return (
                    <UserMFAButton
                        hasMFA={hasMFA}
                        onClick={() => filterBool('hasMFA', hasMFA + '')}
                        tooltip={i(`user.list.table.columns.mfa.${hasMFA ? 'enabled' : 'not-activated'}`)}
                    />
                );
            },
        },
        {
            title: [
                { label: i('user.list.table.columns.dates') },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            className: 'user-list__column-dates',
            optionalRender: DisplayDates,
            render: ({ _updatedAt = 0, _createdAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'user-list__column-actions',
            render: (user): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={actionList([user], (count) => {
                            listRefetch();
                            count && countRefetch();
                        })}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
        render: ({ locked, hasMFA }, checkbox): ReactElement => (
            <UserListCheckboxCell locked={locked} hasMFA={hasMFA} checkbox={checkbox} />
        ),
    });

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showDates, setDefaultDisplay]);

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    usePageTitle(i('page-title.organisation.users', { companyName: orgId }));

    return (
        <THLayout>
            <>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <AddButton
                                    label={i('user.list.header.add-user')}
                                    disabled={!quotas.users?.normal.valid && !quotas.users?.service.valid}
                                    onClick={(): void => setVisibleCreate(true)}
                                />
                            )}

                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}

                            <ExportButton
                                onClick={() => {
                                    setExportDrawerVisible(true);
                                }}
                                objectType="User"
                                selectedItems={dataSource.reduce((acc: string[], data) => {
                                    if (selectedKeys.includes(data['_id'])) {
                                        acc.push(data['_id']);
                                    }
                                    return acc;
                                }, [])}
                                queryParams={queryParams}
                                visible={exportDrawerVisible}
                                afterVisible={setExportDrawerVisible}
                                listNumber={table.count}
                            />

                            <SelectedInfo label={i('common.selected')} count={selectedKeys.length} />

                            {hasPermissions && (
                                <ActionList
                                    actions={actionList(
                                        dataSource.filter((data: { _id: string }) =>
                                            selectedKeys.includes(data['_id']),
                                        ),
                                        (count) => {
                                            listRefetch?.();
                                            table?.setSelectedKeys?.([]);
                                            if (count) {
                                                countRefetch?.();
                                            }
                                        },
                                    )}
                                    scope="list"
                                    hidden={!selectedKeys?.length}
                                />
                            )}
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={(editFilters): void => setLocalFilters(editFilters)}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                                <THFilter
                                    filterConfig={filterManager}
                                    describeField={describe?.[ObjectType.USER]}
                                    attributeFormatter={{
                                        profile: {
                                            name: `profile.${orgId}`,
                                        },
                                    }}
                                >
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="_id"
                        rowSelection={hasPermissions ? rowSelection : undefined}
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('user.none')}>
                                {hasPermissions && (
                                    <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                        {i('user.list.header.add-user')}
                                    </THButton>
                                )}
                            </DisplayEmptyData>
                        }
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>

                {data && visible && (
                    <GlobalUserDrawer
                        visible={visible}
                        onVisibleChange={setVisible}
                        data={data}
                        eventList={eventList}
                        orgId={orgId}
                    />
                )}

                {visibleCreate && (
                    <UserCreate
                        visible={visibleCreate}
                        onVisibleChange={setVisibleCreate}
                        userList={dataSource || []}
                        onFinish={(user): void => {
                            client.UserController.create<UserInput, UserOutput>(user)
                                .then(() => {
                                    openMessage('success', i('notification.user.create'));
                                    listRefetch();
                                    countRefetch();
                                })
                                .catch(handleNotificationError);
                        }}
                        orgId={orgId}
                    />
                )}
            </>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.users');
const pathUser = RouteBuilder.compute('organisation.users');

export const Route: RouteType = {
    path,
    menu: {
        label: 'organisation.layout.nav-bar.users',
        icon: <ICONS.UsersIcon />,
    },
    permissionNeeded: [PermissionsEnum.MANAGE_USER],
    content: <UserList />,
    Context: ListProvider,
    shared: true,
    space: ['admin'],
    breadcrumb: { label: 'breadcrumb.organisation.users' },
};

export const RouteUser: RouteType = {
    ...Route,
    path: pathUser,
    space: ['user'],
};
